import type { ResponsiveImageType } from 'vue-datocms'

type FilterValue = string
type FilterKey = string
type DisplayKey = string
type JoinValue = 'AND' | 'OR'

interface BelongsToFilterGroup {
  filterGroupDisplayName: string
  filterGroupValue: string
  filterKey: FilterKey
  displayKey: DisplayKey
}

interface Filter {
  displayValue: string
  filterValue: FilterValue
  belongsToFilterGroup: BelongsToFilterGroup
}

interface GroupedFiltersItem {
  value: FilterValue
  joinValue: JoinValue
}

interface GroupedFilter {
  displayTitle: string
  groupedFilters: Array<GroupedFiltersItem>
  filterKey: FilterKey
  displayKey: DisplayKey
}

interface FilterJoinOption {
  joinValue: JoinValue
}

interface GroupedFilters {
  [key: string]: GroupedFilter
}

export interface FilterGroup {
  [key: string]: BelongsToFilterGroup & {
    attributeForFacet: string
  }
}

function isFilterOptionType(value: Filter | FilterJoinOption): value is Filter {
  return Object.prototype.hasOwnProperty.call(value, 'belongsToFilterGroup')
}

/**
 *
 * @param datoFilters
 * @returns { 'filterGroupValue': { displayTitle: 'Filter display title', groupedFilters: [ { value: 'filterValue', joinValue: 'OR' }, { value: 'filterValue', joinValue: 'OR' }, ... ], displayKey: 'filterKey', valueKey: 'valueKey' } }
 */
export function groupFilters(
  datoFilters: Array<Filter | FilterJoinOption>
): GroupedFilters {
  const defaultFilters = <GroupedFilters>{}

  datoFilters.forEach((item: Filter | FilterJoinOption, index: number) => {
    if (isFilterOptionType(item)) {
      defaultFilters[item.belongsToFilterGroup.filterGroupValue] =
        defaultFilters[item.belongsToFilterGroup.filterGroupValue] || {
          displayTitle: item.belongsToFilterGroup.filterGroupDisplayName,
          groupedFilters: [],
          filterKey: item.belongsToFilterGroup.filterKey,
          displayKey: item.belongsToFilterGroup.displayKey,
        }

      defaultFilters[
        item.belongsToFilterGroup.filterGroupValue
      ].groupedFilters.push({
        value: item.filterValue ? item.filterValue : item.displayValue,
        joinValue:
          index + 1 < datoFilters.length
            ? datoFilters[index + 1].joinValue || 'OR'
            : 'OR',
      })
    }
  })

  return defaultFilters
}

/**
 *
 * @param defaultFilters
 * @returns An Algolia compatible filter string for Algolia filter search
 *
 * e.g. 'lensTech.filterValue:8ko OR partner:ineos_grenadiers OR partner:mclaren'
 *
 */
export function buildAlgoliaFilterString(
  defaultFilters: Array<Filter | FilterJoinOption>,
  pageSlug: string | undefined
): string {
  const groupedDefaultFilters = groupFilters(defaultFilters)
  let filterString = ''
  let nextJoinValueToUse = 'OR'

  Object.entries(groupedDefaultFilters).map(([key, filterGroup], keyIndex) => {
    filterGroup.groupedFilters.forEach((groupedFilter, index) => {
      if (index === 0 && keyIndex === 0) {
        filterString +=
          key +
          (filterGroup.filterKey ? '.' + filterGroup.filterKey + ':' : ':') +
          groupedFilter.value

        nextJoinValueToUse = groupedFilter.joinValue
      } else {
        filterString +=
          ' ' +
          nextJoinValueToUse +
          ' ' +
          key +
          (filterGroup.filterKey ? '.' + filterGroup.filterKey + ':' : ':') +
          groupedFilter.value

        nextJoinValueToUse = groupedFilter.joinValue
      }
    })
  })

  if (filterString) {
    if(pageSlug) {
      filterString += ' AND pages:/' + pageSlug.replaceAll('__', '/') + ' AND (inStock:1 OR onPreorder:1)'
    } else {
      filterString += ' AND (inStock:1 OR onPreorder:1) OR hideWhenOutOfStock:false'
    }

    return filterString
  }

  if (pageSlug) {
    return ' AND (pages:/' + pageSlug?.replaceAll('__', '/') + ' AND (inStock:1 OR onPreorder:1))'
  }

  return '(inStock:1 OR onPreorder:1) OR hideWhenOutOfStock:false'
}

/**
 *
 * @param datoFilterGroups
 * @returns { 'lensTech': { filterGroupDisplayName: string, filterGroupValue: string, filterKey: FilterKey, displayKey: DisplayKey }, ... }
 * @description Returns DatoFilterGroups as an object for easier access to filter group properties.
 */
export function normaliseFilterGroups(
  datoFilterGroups: Array<BelongsToFilterGroup>
): FilterGroup {
  const normalisedFilterGroups = <FilterGroup>{}

  datoFilterGroups.forEach((datoFilterGroup) => {
    normalisedFilterGroups[datoFilterGroup.filterGroupValue] = datoFilterGroup
  })

  return normalisedFilterGroups
}

/**
 *
 * @param defaultFilters
 * @param datoFilterGroups
 * @returns { 'lensTech': { filterGroupDisplayName: string, filterGroupValue: string, filterKey: FilterKey, displayKey: DisplayKey }, ... }
 * @description Returns same as normaliseFilterGroups function but removes any keys that are defined in default filters
 * as facets should not show for any filter that was set as a default filter that has only one filter value set for that group.
 */
export function buildFacetsToDisplay(
  defaultFilters: Array<Filter>,
  datoFilterGroups: Array<BelongsToFilterGroup>
): FilterGroup {
  const groupedDefaultFilters = groupFilters(defaultFilters)
  const allFilterGroups = normaliseFilterGroups(datoFilterGroups)

  return Object.keys(allFilterGroups)
    .filter((filterGroupKey) => {
      if (Object.keys(groupedDefaultFilters).includes(filterGroupKey)) {
        if (groupedDefaultFilters[filterGroupKey].groupedFilters.length > 1)
          return true

        return false
      }

      return !Object.keys(groupedDefaultFilters).includes(filterGroupKey)
    })
    .reduce((obj, key) => {
      obj[key] = allFilterGroups[key]

      function buildAttributeForFacet() {
        if (obj[key].displayKey) {
          return obj[key].filterGroupValue + '.' + obj[key].displayKey
        }
      
        return obj[key].filterGroupValue
      }

      obj[key].attributeForFacet = buildAttributeForFacet()

      return obj
    }, <FilterGroup>{})
}

export function filterValidLensTechDisplay (lensTech: {
  filterValue: string
  displayValue: string
}) {
  if (!lensTech) return

  if (['4ko', '4kosnow', 'rx', 'rxClear'].includes(lensTech.filterValue)) return

  // Iris is tied to the lens colour, this stops it showing twice in the cards
  if(lensTech.filterValue === '8ko_ir') {
    return {
      filterValue: '8ko_ir',
      displayValue: '8KO<sup>®</sup>'
    }
  }

  return lensTech
}

interface FilterBanner {
  id: string
  bannerPosition: 'in-results-portrait' | 'in-results-landscape' | 'above-results'
  portraitBannerStyle: 'custom' | 'lifetime-guarantee' | 'carbon-neutral'
  landscapePortraitStyle: 'custom'
  portraitBannerImage: ResponsiveImageType
  portraitBannerBackgroundMode: 'cover' | 'contain' | 'fill'
  portraitBannerMobileImage: ResponsiveImageType
  portraitBannerMobileBackgroundMode: 'cover' | 'contain' | 'fill'
  landscapeBannerImage: ResponsiveImageType
  bannerLink: string
  includeOn: Array<{id: string }>
  listPosition: 1 | 2 | 3
  content?: object
  slug: string
}

export function buildBannersToDisplay (
  allFilterBanners: Array<FilterBanner>,
  currentPageId: string,
  inheritGlobalBanners: boolean = true
) {
  const inResultsLanscapeBanners: Array<FilterBanner> = []
  const inResultsPortraitBanners: Array<FilterBanner> = []
  const aboveResultsBanners: Array<FilterBanner> = []

  function buildBannerObject (filterBanner: FilterBanner) {
    if (
      filterBanner.includeOn.length &&
      filterBanner.includeOn.findIndex(({ id }) => id === currentPageId) < 0
    ) {
      return
    }

    // If we don't want to inherit global banners and the banner doesn't have any includeOn values, return
    if (!inheritGlobalBanners && !filterBanner.includeOn.length) return

    return {
      id: filterBanner.id,
      bannerStyle: filterBanner.portraitBannerStyle,
      backgroundImage: filterBanner.portraitBannerImage,
      backgroundMode: filterBanner.portraitBannerBackgroundMode,
      mobileBackgroundImage: filterBanner.portraitBannerMobileImage,
      mobileBackgroundMode: filterBanner.portraitBannerMobileBackgroundMode,
      bannerLink: filterBanner.bannerLink,
      includeOn: filterBanner.includeOn,
      listPosition: filterBanner.listPosition,
      content: filterBanner.content,
      slug: filterBanner.slug
    }
  }

  allFilterBanners.forEach(filterBanner => {
    if (filterBanner.bannerPosition === 'in-results-portrait') {
      inResultsPortraitBanners.push(buildBannerObject(filterBanner))
    } else if (filterBanner.bannerPosition === 'in-results-landscape') {
      inResultsLanscapeBanners.push(buildBannerObject(filterBanner))
    } else if(filterBanner.bannerPosition === 'above-results') {
      aboveResultsBanners.push(buildBannerObject(filterBanner))
    }
  })

  return {
    inResultsPortraitBanners: inResultsPortraitBanners.filter(a => a),
    inResultsLanscapeBanners: inResultsLanscapeBanners.filter(a => a),
    aboveResultsBanners: aboveResultsBanners.filter(a => a)
  }
}
